import { css, styled } from 'next-yak';

import { Button } from '@xing-com/button';
import { createPolymorphicElement } from '@xing-com/polymorphic-element';
import {
  cornerRadiusL,
  scale030,
  scale080,
  scale090,
  scale130,
  scale160,
  spaceL,
  spaceM,
  spaceS,
  spaceXL,
  spaceXS,
} from '@xing-com/tokens';

import type { FacepileSize, FacepileVariant } from './';

type FacepileTransientProps = {
  $size: FacepileSize;
  $variant: FacepileVariant;
};

type CounterButtonTransientProps = {
  $size: FacepileSize;
};

// *** Sizes ***
export const sizes = {
  small: {
    default: css`
      margin-right: ${spaceXS};
    `,
    condensed: css`
      margin-right: ${spaceXS};
      &:nth-child(n + 2) {
        margin-left: calc(calc(${spaceM} * -1) + calc(${spaceXS} * -1));
      }
    `,
    counterButton: css`
      && {
        height: ${scale130};
        margin-left: ${spaceM};
      }
    `,
  },
  medium: {
    default: css`
      margin-right: ${scale030};
    `,
    condensed: css`
      margin-right: ${scale030};
      &:nth-child(n + 2) {
        margin-left: calc(calc(${spaceL} * -1) + calc(${scale030}) * -1);
      }
    `,
    counterButton: css`
      && {
        font-size: ${scale080};
        height: ${scale160};
        margin-left: ${spaceL};
      }
    `,
  },
  large: {
    default: css`
      margin-right: ${scale030};
    `,
    condensed: css`
      margin-right: ${scale030};
      &:nth-child(n + 2) {
        margin-left: calc(calc(${spaceXL} * -1) + calc(${spaceS}) * -1);
      }
    `,
    counterButton: css`
      && {
        font-size: ${scale090};
        height: ${scale160};
        margin-left: ${spaceXL};
      }
    `,
  },
};

// *** Components ***
const FacepileContainerElement = createPolymorphicElement('ul');

export const FacepileContainer = styled(FacepileContainerElement)`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  list-style: none;
  margin: 0;
  padding: 0;
`;

export const Facepile = styled.li<FacepileTransientProps>`
  display: flex;

  &:nth-last-of-type(1) {
    margin-right: 0;
  }

  ${({ $size, $variant }) => {
    switch ($variant) {
      case 'condensed':
        switch ($size) {
          case 'small':
            return sizes.small.condensed;
          case 'medium':
            return sizes.medium.condensed;
          case 'large':
            return sizes.large.condensed;
          default:
            return $size satisfies undefined;
        }
      default:
        $variant satisfies undefined;
        switch ($size) {
          case 'small':
            return sizes.small.default;
          case 'medium':
            return sizes.medium.default;
          case 'large':
            return sizes.large.default;
          default:
            return $size satisfies undefined;
        }
    }
  }}
`;

export const CounterButton = styled(Button)<CounterButtonTransientProps>`
  && {
    min-width: 0;
    padding: 0;
    border-radius: ${cornerRadiusL};
    width: 100%;
    height: 100%;
  }

  ${({ $size }) => {
    switch ($size) {
      case 'small':
        return sizes.small.counterButton;
      case 'medium':
        return sizes.medium.counterButton;
      case 'large':
        return sizes.large.counterButton;
      default:
        return $size satisfies undefined;
    }
  }};
`;

export const FacepileButton = styled(Button)`
  && {
    border-radius: ${cornerRadiusL};
    height: 100%;
    min-width: auto;
    overflow: visible;
    padding: 0;
    width: 100%;
  }
`;

export const FacepileButtonContainer = styled.li``;
